import React, { useState, useMemo } from "react";
import { Box, Typography, IconButton, Button } from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import AlertToast from "./Alert";
import InputField from "./InputField";
import dayjs from "dayjs";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TimeField from "./TimeField";
interface DateRangeFilterProps {
    onCancel: () => void;
    onApply: (from: string, to: string, fromTime?: string, toTime?: string) => void;
    initialFrom?: string;
    initialTo?: string;
    initialFromTime?: string;
    initialToTime?: string;
    initialYear?: number;
    initialMonth?: number;
    addTime?: boolean;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
    onCancel,
    onApply,
    initialFrom,
    initialTo,
    initialFromTime = '00:00:00',
    initialToTime = '23:59:59',
    initialYear,
    initialMonth,
    addTime = false
}) => {
    const today = new Date();
    const defaultDate = today.toISOString().split("T")[0];

    const [year, setYear] = useState<number>(initialYear || today.getFullYear());
    const [month, setMonth] = useState<number>(
        typeof initialMonth === "number" ? initialMonth : today.getMonth()
    );
    const [fromDate, setFromDate] = useState<string>(initialFrom || defaultDate);
    const [toDate, setToDate] = useState<string>(initialTo || defaultDate);
    const [fromTime, setFromTime] = useState<string>(initialFromTime || "00:00:00");
    const [toTime, setToTime] = useState<string>(initialToTime || "23:59:59");
    const [selectingFrom, setSelectingFrom] = useState<boolean>(true);
    const [alertOpen, setAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>("");
    const [alertType, setAlertType] = useState<"error" | "success" | "warning" | "info">("error");

    const months: string[] = useMemo(
        () => [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December",
        ],
        []
    );

    const daysInMonth: number = useMemo(() => new Date(year, month + 1, 0).getDate(), [year, month]);

    const isFutureDate = (dateString: string): boolean => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time part for accurate comparison
        const date = new Date(dateString);
        date.setHours(0, 0, 0, 0); // Also reset selected date
        return date > today;
    };

    const handleDateSelection = (day: number) => {
        const selectedDate = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        if (isFutureDate(selectedDate)) {
            return;
        }
        if (selectingFrom) {
            setFromDate(selectedDate);
            setToDate(""); // Reset `toDate`
            setSelectingFrom(false);
        } else {
            if (selectedDate < fromDate) {
                // Automatically swap `fromDate` and `toDate` if needed
                setFromDate(selectedDate);
                setToDate(fromDate);
            } else {
                setToDate(selectedDate);
            }
            setSelectingFrom(true);
        }
    };

    const handleApply = () => {
        if (isFutureDate(fromDate) || isFutureDate(toDate)) {
            setAlertMessage("Cannot select future dates");
            setAlertType("error");
            setAlertOpen(true);
            return;
        }
        const from = new Date(fromDate);
        const to = new Date(toDate);
        if (
            isNaN(from.getTime()) ||
            isNaN(to.getTime()) ||
            !/^\d{4}-\d{2}-\d{2}$/.test(fromDate) ||
            !/^\d{4}-\d{2}-\d{2}$/.test(toDate)
        ) {
            setAlertMessage("One or both dates are in an invalid format.");
            setAlertType("error");
            setAlertOpen(true);
            return;
        }
        const diffInDays = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));

        if (to < from) {
            setAlertMessage("Invalid date range: 'To' date cannot be earlier than 'From' date.");
            setAlertType("error");
            setAlertOpen(true);
            return;
        }

        if (diffInDays > 30) {
            setAlertMessage("Date range cannot exceed 30 days.");
            setAlertType("error");
            setAlertOpen(true);
            return;
        }

        onApply(fromDate, toDate, fromTime, toTime);
    };

    const isInvalidRange =
        !/^\d{4}-\d{2}-\d{2}$/.test(fromDate) ||
            !/^\d{4}-\d{2}-\d{2}$/.test(toDate) ||
            isNaN(new Date(fromDate).getTime()) ||
            isNaN(new Date(toDate).getTime()) ||
            toDate && fromDate > toDate ? true : false;

    return (
        <>
            <AlertToast message={alertMessage} type={alertType} open={alertOpen} onClose={() => setAlertOpen(false)} />

            <Box className="flex flex-col gap-2" sx={{ width: 300, backgroundColor: "var(--date-range-bg)", borderRadius: 2, p: "10px" }}>
                {/* Year Selection */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <IconButton color={"primary"} onClick={() => setYear(year - 1)} size="small">
                        <ArrowBackIos fontSize="small" />
                    </IconButton>
                    <Typography
                        variant="button"
                        fontWeight={600}
                        color={"primary"}
                        sx={{ cursor: "pointer" }}
                    >
                        {year}
                    </Typography>
                    <IconButton color={"primary"} onClick={() => setYear(year + 1)} size="small" disabled={year === new Date().getFullYear() && month === new Date().getMonth()}>
                        <ArrowForwardIos fontSize="small" />
                    </IconButton>
                </Box>

                {/* Month Selection */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <IconButton color={"primary"} onClick={() => setMonth((month + 11) % 12)} size="small">
                        <ArrowBackIos fontSize="small" />
                    </IconButton>
                    <Typography
                        variant="button"
                        fontWeight={600}
                        color={"primary"}
                        sx={{ cursor: "pointer" }}
                    >
                        {months[month]}
                    </Typography>
                    <IconButton color={"primary"} onClick={() => setMonth((month + 1) % 12)} size="small" disabled={year === new Date().getFullYear() && month === new Date().getMonth()}>
                        <ArrowForwardIos fontSize="small" />
                    </IconButton>
                </Box>

                {/* Days Selection */}
                <Box display="flex" flexWrap="wrap">
                    {Array.from({ length: daysInMonth }, (_, i) => {
                        const day = i + 1;
                        const selectedDate = `${year}-${String(month + 1).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
                        const isFuture = isFutureDate(selectedDate);
                        const isStart = selectedDate === fromDate;
                        const isEnd = selectedDate === toDate;
                        const isInRange = fromDate && toDate && selectedDate > fromDate && selectedDate < toDate;

                        return (
                            <Box
                                key={i + 1}
                                onClick={() => !isFuture && handleDateSelection(day)}
                                sx={(theme) => ({
                                    width: "14%",
                                    padding: "6px",
                                    textAlign: "center",
                                    cursor: isFuture ? "not-allowed" : "pointer",
                                    borderRadius: 1,
                                    transition: "0.05s",
                                    opacity: isFuture ? 0.4 : 1,
                                    backgroundColor: isInRange || isStart || isEnd ? theme.palette.action.hover : "transparent",
                                    color: isStart || isEnd || isInRange ? theme.palette.text.primary : "inherit",
                                    "&:hover": { backgroundColor: theme.palette.action.hover, color: theme.palette.text.primary },
                                })}
                            >
                                {day}
                            </Box>
                        );
                    })}
                </Box>

                {/* Date and Time Inputs */}
                <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
  {/* From Date Input with Clear Button */}
  <Box position="relative" width="100%">
    <InputField
      label="From Date"
      name="fromDate"
      value={fromDate}
      onChange={(e) => {
        const value = e.target.value;
        if (!isFutureDate(value)) {
          setFromDate(value);
        }
      }}
      type="date"
      status={isInvalidRange ? "error" : undefined}
      errorMessage={
        !/^\d{4}-\d{2}-\d{2}$/.test(fromDate)
          ? "'From' date format is invalid"
          : toDate && fromDate > toDate
            ? "'From' date cannot be after 'To' date"
            : ""
      }
    />
    {fromDate && (
      <IconButton
        size="small"
        onClick={() => setFromDate('')}
        sx={{
          position: 'absolute',
          right: -5,
          top: 18,
          color: 'error.main',
          background: '#fff',
          zIndex: 2,
          padding: '1px',
          fontSize: '16px',
          width: 22,
      height: 22,
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          
        }}
        title="Clear From Date"
      >
        &#10005;
      </IconButton>
    )}
  </Box>
  {/* To Date Input with Clear Button */}
  <Box position="relative" width="100%">
    <InputField
      label="To Date"
      name="toDate"
      value={toDate}
      onChange={(e) => {
        const value = e.target.value;
        if (!isFutureDate(value)) {
          setToDate(value);
        }
      }}
      type="date"
      status={isInvalidRange ? "error" : undefined}
      errorMessage={
        !/^\d{4}-\d{2}-\d{2}$/.test(toDate)
          ? "'To' date format is invalid"
          : toDate && fromDate > toDate
            ? "'To' date cannot be before 'From' date"
            : ""
      }
    />
    {toDate && (
      <IconButton
        size="small"
        onClick={() => setToDate('')}
        sx={{
          position: 'absolute',
          right: -5,
          top: 18,
          color: 'error.main',
          background: '#fff',
          zIndex: 2,
          padding: '1px',
          fontSize: '16px',
          width: 22,
      height: 22,
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}
        title="Clear To Date"
      >
        &#10005;
      </IconButton>
    )}
  </Box>
</Box>
                    {initialFromTime && initialToTime && addTime &&
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                <TimeField
                                    minutesStep={1}
                                    label="From Time"
                                    name="fromTime"
                                    value={fromTime}
                                    onChange={(time) => setFromTime(time || "00:00:00")}
                                    showSeconds={true}
                                />
                                {/* <TimeField
                                    minutesStep={1}
                                    label="From Time"
                                    name="fromTime"
                                    value={dayjs(fromTime)}
                                    onChange={(time) => setFromTime(time ? time.format('HH:mm:ss') : "00:00:00")}
                                /> */}
                                {/* <TimeField
                                    minutesStep={1}
                                    label="To Time"
                                    name="toTime"
                                    value={dayjs(toTime)}
                                    onChange={(time) => setToTime(time ? time.format('HH:mm:ss') : "00:00:00")}
                                /> */}
                                <TimeField
                                    minutesStep={1}
                                    label="To Time"
                                    name="toTime"
                                    value={toTime}
                                    onChange={(time) => setToTime(time || "23:59:59")}
                                    showSeconds={true}
                                />
                            </Box>
                        </LocalizationProvider>
                    }
                </Box>

                {/* Buttons */}
                <Box display="flex" justifyContent="space-between">
                    <Button onClick={onCancel} variant="outlined" color="error" sx={(theme) => ({ borderRadius: 2, border: `1.5px solid ${theme.palette.error.main}` })}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleApply}
                        variant="contained"
                        color="primary"
                        disabled={isInvalidRange}
                        sx={(theme) => ({ borderRadius: 2, backgroundColor: theme.palette.primary.main })}>
                        Apply
                    </Button>
                </Box>
            </Box>
        </>
    );
};

export default DateRangeFilter;